import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CustomerInterface } from '@contracts/interfaces/customer.interface';
import { CustomerService } from '../../services/customer.service';
import { CustomerDialogService } from '../customer/customer.service.dialog';
import { phone_number } from '../../tools/phone-number';

export interface CustomerSearchDialogData {
  searchText?: string;
}

@Component({
  selector: 'ap-customer-search-dialog',
  templateUrl: './customer-search.dialog.html',
  styleUrls: ['./customer-search.dialog.scss']
})
export class CustomerSearchDialogComponent {

  searchText = '';
  searchMode: 'name' | 'mobile' = 'name';
  customers: CustomerInterface[] = [];
  isSearching = false;
  private searchTimer: any = null;
  private searchRequestId = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CustomerSearchDialogData,
    public dialogRef: MatDialogRef<CustomerSearchDialogComponent>,
    private customerService: CustomerService,
    private customerDialogService: CustomerDialogService
  ) {
    this.searchText = data?.searchText || '';

    if (this.searchText) {
      this.findCustomers(this.searchText);
    }
  }

  queueCustomerSearch(searchText: string) {
    this.searchText = searchText;

    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }

    if (!searchText) {
      this.customers = [];
      this.isSearching = false;
      return;
    }

    this.searchTimer = setTimeout(() => {
      this.findCustomers(searchText);
    }, 200);
  }

  changeSearchMode(mode: 'name' | 'mobile') {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }

    this.searchMode = mode;
    this.searchText = '';
    this.customers = [];
    this.isSearching = false;
    this.searchRequestId++;
  }

  findCustomers(searchText: string) {
    const requestId = ++this.searchRequestId;
    this.searchText = searchText;
    this.isSearching = true;

    const request = this.searchMode === 'mobile'
      ? this.customerService.find(this.buildMobileSearchQuery(searchText), 0, 15)
      : this.customerService.search(searchText, 15);

    request.subscribe({
      next: (customers: CustomerInterface[] | any) => {
        if (requestId !== this.searchRequestId) {
          return;
        }

        this.customers = customers || [];
        this.isSearching = false;
      },
      error: () => {
        if (requestId !== this.searchRequestId) {
          return;
        }

        this.isSearching = false;
      }
    });
  }

  trackByCustomer(index: number, customer: CustomerInterface) {
    return customer?._id || `${customer?.name}-${customer?.phone}-${index}`;
  }

  pickCustomer(customer: CustomerInterface) {
    this.dialogRef.close(customer);
  }

  createCustomer(customerName = '') {
    this.customerDialogService.launchDialog(null, customerName, (customer: CustomerInterface) => {
      if (customer?._id) {
        this.dialogRef.close(customer);
      }
    });
  }

  private buildMobileSearchQuery(searchText: string) {
    const raw = this.escapeRegex(searchText);
    const normalized = this.escapeRegex(phone_number(searchText));
    const terms = raw === normalized ? [raw] : [raw, normalized];
    const phoneQueries = [];

    terms.forEach(term => {
      if (!term) {
        return;
      }

      phoneQueries.push({ phone: { $regex: term, $options: 'i' } });
      phoneQueries.push({ phone2: { $regex: term, $options: 'i' } });
    });

    return { $or: phoneQueries };
  }

  private escapeRegex(value: string) {
    return (value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
